/**
 * @module components/analysis/ReviewSection
 * @description
 *   Metric-aligned AI review subject cards (Card inset + IconTile).
 *   Prefers `results.reviewSubjects`; falls back to legacy sentence-split
 *   `review` (excluding similar-artists/books lines → SimilarWorksSection).
 *   Layout: 2-col grid (md+); odd last card spans full width.
 *
 * @see docs/SUBJECT_CRITIQUE_CARDS.md
 */

import React, { useState } from 'react';
import {
  Mic,
  BookOpen,
  Sliders,
  PenLine,
  Heart,
  AudioLines,
  BookMarked,
  Users,
  Feather,
  Eye,
  Library,
} from 'lucide-react';
import { Card, SectionHeader, IconTile } from '../ui';

const SUBJECT_ICONS = {
  production: Sliders,
  mixing: AudioLines,
  vocals: Mic,
  lyrics: PenLine,
  emotion: Heart,
  narrative_structure: BookMarked,
  character_development: Users,
  writing_style: Feather,
  readability: Eye,
};

const SUBJECT_GRADIENTS = [
  'bg-gradient-to-r from-apple-blue to-apple-purple',
  'bg-gradient-to-r from-apple-purple to-apple-pink',
  'bg-gradient-to-r from-apple-green to-apple-teal',
  'bg-gradient-to-r from-apple-orange to-apple-yellow',
];

/** Similar-works lines belong in SimilarWorksSection, not AI Review cards. */
function isSimilarWorksParagraph(paragraph) {
  const text = String(paragraph || '').trim();
  if (!text) return true;
  return /fans of this (?:style|genre) might also enjoy/i.test(text);
}

/**
 * @param {object} props
 * @param {{ review?: string, reviewSubjects?: { id: string, title: string, body: string }[] }} props.results
 * @param {'music'|'book'} props.craftType
 */
export default function ReviewSection({ results, craftType }) {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const subjects = Array.isArray(results.reviewSubjects)
    ? results.reviewSubjects.filter((s) => s && s.body)
    : [];
  const useSubjects = subjects.length > 0;
  const legacyParagraphs =
    !useSubjects && results.review
      ? results.review
          .split('\n\n')
          .map((p) => p.trim())
          .filter((p) => p && !isSimilarWorksParagraph(p))
      : [];

  return (
    <Card className="animate-slide-up glow-effect-blue hover-lift">
      <SectionHeader
        stacked={false}
        collapsed={sectionCollapsed}
        onToggleCollapse={() => setSectionCollapsed((c) => !c)}
        icon={
          craftType === 'music' ? (
            <Mic className="h-6 w-6 text-white" />
          ) : (
            <BookOpen className="h-6 w-6 text-white" />
          )
        }
        title="AI Review"
        subtitle={
          craftType === 'music'
            ? 'Metric-aligned critique and advice for each technical score.'
            : 'Metric-aligned critique and advice for each literary score.'
        }
      />
      {!sectionCollapsed ? (
      <div className="max-w-none">
        <div className="text-critic-text-primary leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-5">
          {useSubjects
            ? subjects.map((subject, index) => {
                const Icon = SUBJECT_ICONS[subject.id] || (craftType === 'music' ? Mic : Library);
                const isLastOdd =
                  subjects.length % 2 === 1 && index === subjects.length - 1;
                return (
                  <Card
                    key={subject.id || index}
                    variant="inset"
                    className={`animate-slide-up md:p-7 h-full ${
                      isLastOdd ? 'md:col-span-2' : ''
                    }`}
                    style={{ animationDelay: `${index * 120}ms` }}
                  >
                    <div className="flex items-center space-x-3 mb-4 min-w-0">
                      <IconTile
                        size="sm"
                        gradient={SUBJECT_GRADIENTS[index % SUBJECT_GRADIENTS.length]}
                      >
                        <Icon className="h-4 w-4 text-white" aria-hidden="true" />
                      </IconTile>
                      <h3 className="font-display text-base font-semibold text-critic-text-primary truncate">
                        {subject.title}
                      </h3>
                    </div>
                    <p className="text-base md:text-[1.05rem] leading-relaxed font-normal text-critic-text-secondary whitespace-pre-wrap">
                      {subject.body}
                    </p>
                  </Card>
                );
              })
            : legacyParagraphs.map((paragraph, index) => {
                const isLastOdd =
                  legacyParagraphs.length % 2 === 1 &&
                  index === legacyParagraphs.length - 1;
                return (
                  <Card
                    key={index}
                    variant="inset"
                    className={`animate-slide-up md:p-7 h-full ${
                      isLastOdd ? 'md:col-span-2' : ''
                    }`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <p className="text-base md:text-[1.05rem] leading-relaxed font-normal text-critic-text-secondary">
                      {paragraph}
                    </p>
                  </Card>
                );
              })}
        </div>
      </div>
      ) : null}
    </Card>
  );
}
